import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const isInternalHref = (href) => typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');

/**
 * Renders assistant Markdown (GFM tables, lists, links, code) with MUI
 * typography. Internal links (/dashboard/...) navigate inside the SPA.
 */
const AssistantMarkdown = ({ children }) => {
  const navigate = useNavigate();

  const components = {
    p: ({ node, ...props }) => <Typography variant="body2" component="p" sx={{ mb: 1, '&:last-child': { mb: 0 } }} {...props} />,
    h1: ({ node, ...props }) => <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />,
    h2: ({ node, ...props }) => <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />,
    h3: ({ node, ...props }) => <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />,
    h4: ({ node, ...props }) => <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />,
    ul: ({ node, ...props }) => <Box component="ul" sx={{ my: 0.5, pl: 2.5 }} {...props} />,
    ol: ({ node, ...props }) => <Box component="ol" sx={{ my: 0.5, pl: 2.5 }} {...props} />,
    li: ({ node, ...props }) => <Typography variant="body2" component="li" sx={{ mb: 0.25 }} {...props} />,
    strong: ({ node, ...props }) => <Box component="strong" sx={{ fontWeight: 700 }} {...props} />,
    a: ({ node, href, children: linkChildren, ...props }) => {
      if (isInternalHref(href)) {
        return (
          <Link
            component="button"
            type="button"
            onClick={() => navigate(href)}
            sx={{ verticalAlign: 'baseline', textAlign: 'left' }}
            {...props}
          >
            {linkChildren}
          </Link>
        );
      }
      return (
        <Link href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {linkChildren}
        </Link>
      );
    },
    // react-markdown v9 no longer passes `inline`; block code arrives wrapped in <pre>.
    code: ({ node, className, children: codeChildren, ...props }) => (
      <Box
        component="code"
        sx={{ px: 0.5, py: 0.125, borderRadius: 0.5, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.85em', whiteSpace: 'pre-wrap' }}
        {...props}
      >
        {codeChildren}
      </Box>
    ),
    pre: ({ node, ...props }) => (
      <Box
        component="pre"
        sx={{ m: 0, mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover', overflowX: 'auto', '& code': { p: 0, bgcolor: 'transparent' } }}
        {...props}
      />
    ),
    table: ({ node, ...props }) => (
      <Box sx={{ overflowX: 'auto', mb: 1 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem',
            '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left', verticalAlign: 'top' },
            '& th': { bgcolor: 'action.hover', fontWeight: 700 },
          }}
          {...props}
        />
      </Box>
    ),
    blockquote: ({ node, ...props }) => (
      <Box component="blockquote" sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 1.5, ml: 0, my: 1, color: 'text.secondary' }} {...props} />
    ),
    hr: () => <Box component="hr" sx={{ border: 0, borderTop: '1px solid', borderColor: 'divider', my: 1 }} />,
  };

  return (
    <Box sx={{ wordBreak: 'break-word' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {String(children || '')}
      </ReactMarkdown>
    </Box>
  );
};

export default AssistantMarkdown;
